import { useNavigate, useSearch } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import {
  Card,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { PageSkeleton } from "@/components/PageSkeleton";
import { Shell } from "@/components/Shell";
import { SignIn } from "@/features/session/SignIn";
import { useMe } from "@/features/session/useMe";
import { Bevoegdheden } from "@/features/stamgegevens/Bevoegdheden";
import { keepScrollPosition } from "@jbnl/shared-ui";

/**
 * §4.3.1's precondition: without a row here an ondersteuner cannot sign up at all, so this has
 * to be filled before an inschrijfperiode for ondersteuners opens.
 */
export function BevoegdhedenPage() {
  const { t } = useTranslation(["paginas", "navigatie", "common"]);
  const { data: me, isPending } = useMe();
  const {
    persoon: persoonId,
    q,
    pagina,
    functie,
  } = useSearch({ from: "/bevoegdheden" });
  const navigate = useNavigate({ from: "/bevoegdheden" });

  if (isPending) {
    return (
      <Shell>
        <PageSkeleton />
      </Shell>
    );
  }

  if (!me) {
    return (
      <Shell>
        <SignIn />
      </Shell>
    );
  }

  if (!me.isBeheerder) {
    return (
      <Shell name={me.name} letterhead>
        <Card>
          <CardHeader>
            <CardTitle>{t("common:doors.noAccess")}</CardTitle>
            <CardDescription>
              {t("common:doors.noAccessDescription")}
            </CardDescription>
          </CardHeader>
        </Card>
      </Shell>
    );
  }

  return (
    <Shell name={me.name} wide>
      <header className="mb-6 space-y-1">
        <h1 className="text-2xl font-bold tracking-[-0.02em]">
          {t("navigatie:paginas.bevoegdheden.label")}
        </h1>
        <p className="text-sm text-muted-foreground">
          {t("bevoegdheden.uitleg")}
        </p>
      </header>

      <Bevoegdheden
        functieIds={functie ? functie.split(",") : []}
        onFuncties={(ids) =>
          void navigate({
            ...keepScrollPosition,
            search: (previous) => ({
              ...previous,
              functie: ids.length > 0 ? ids.join(",") : undefined,
              // A narrower set is a shorter one, so page 3 of the old list would land past
              // the end of the new one.
              pagina: undefined,
            }),
          })
        }
        term={q ?? ""}
        onZoek={(term) =>
          void navigate({
            ...keepScrollPosition,
            search: (previous) => ({
              ...previous,
              persoon: persoonId,
              q: term || undefined,
            }),
          })
        }
        persoonId={persoonId ?? null}
        onKies={(id) =>
          void navigate({
            ...keepScrollPosition,
            search: (previous) => ({
              ...previous,
              persoon: id ?? undefined,
              q,
            }),
          })
        }
        pagina={pagina}
        onPaginaChange={(next) =>
          void navigate({
            search: (previous) => ({ ...previous, pagina: next }),
            ...keepScrollPosition,
          })
        }
      />
    </Shell>
  );
}
